import { mapActions, mapState } from 'vuex';
import { VueAgile } from 'vue-agile';
import { POST_LOADER_DELAY, countries } from '@/constants';
import { ICategory } from '@/services/asset';
import { IMaterial } from '@/services/material';
import checks, { MaterialChecks } from '@/services/checks';
import { GridColumn, GridForm, GridRow } from '@/components/grid';
import AssetManagerBox from '@/components/asset-manager/AssetManagerBox.vue';
import AssetManagerRegion from '@/components/asset-manager/AssetManagerRegion.vue';
import AssetManagerSlideshow from '@/components/asset-manager/AssetManagerSlideshow.vue';
import CheckList from '@/components/CheckList.vue';
import ChromeEditor from '@/components/chrome/Editor.vue';
import ChromeEditorPanel from '@/components/chrome/EditorPanel.vue';
import FieldBase from '@/components/field/Base.vue';
import FullscreenButton from '@/components/FullscreenButton.vue';
import PricingTable from '@/components/PricingTable.vue';
import WashIcons from '@/components/wash-icons/WashIcons.vue';
import WashSelector from '@/components/wash-icons/WashSelector.vue';
import washSymbols from '@/components/wash-icons/symbols';
import PhysicalProps from './PhysicalProps.vue';
import ProductUsePanel from '@/components/product/ProductUsePanel.vue';
import PropsTable from '@/components/PropsTable.vue';
import ShowGuide from '@/components/ShowGuide.vue';
import SidebarPanel from '@/components/sidebar/SidebarPanel.vue';
import VendorEditorPanel from '@/components/vendor/VendorEditorPanel.vue';
import messages from '../../messages';
import { askDelete, createMaterial } from '../../common';
import checklistActions from './checks';
import settings from './settings';

export default (storeName: string) => ({
  data() {
    const self = this;
    return {
      checkListActionss: checklistActions(this),
      fetched: false,
      guide: [
        {
          point: '[class*=Component_header]',
        },
        {
          ref: 'descriptive',
          onEnter: () => {
            self.selectPanel('info');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          point: '#physicalProps',
          dialog: {
            selector: '#descriptiveInfo',
            content: 'Add basic information to help identify and describe your material. This will help build up a richer material library in the long run.',
          },
        },
        {
          ref: 'physical',
          point: '#assetRegion',
          dialog: {
            selector: self.$store.state.isMobile ? '#physicalInfo' : '#physicalProps',
            content: 'Physical properties give more clarity to how a material will behave when used.',
          },
        },
        {
          ref: 'assets',
          onEnter: () => {
            self.$store.state.isMobile
              ? self.toggleSidebar(false)
              : self.selectPanel('assets');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          point: '[class*="SidebarWrapper_toolbar"] [title="Washing"]',
          dialog: {
            selector: '#assetRegion',
            content: 'Add detailed images of your material including close-up shots and shots displaying repeating patterns if applicable.',
          },
        },
        {
          ref: 'washing',
          onEnter: () => {
            self.selectPanel('washing');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          point: '[class*="SidebarWrapper_toolbar"] [title="Vendors"]',
          dialog: {
            selector: '[class*="SidebarWrapper_panelsOuter"]',
            content: 'Add washing information to understand how the material should be cared for.',
          },
        },
        {
          ref: 'vendors',
          onEnter: () => {
            self.selectPanel('vendors');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          point: '#pricingTable',
          dialog: {
            selector: '[class*="SidebarWrapper_panelsOuter"]',
            content: 'Add the manufacturers and suppliers of this material to help build a view of your supply chain.',
          },
        },
        {
          ref: 'pricing',
          point: '#propsTable',
          onEnter: () => {
            // show pricing table when sidebar hidden
            if (self.$store.state.isMobile) {
              self.toggleSidebar(false);
              return new Promise(resolve => setTimeout(resolve, 250));
            }
            return Promise.resolve();
          },
          dialog: {
            selector: '#pricingTable',
            content: 'Add price information such as price per unit length, discount prices etc',
          },
        },
        {
          ref: 'properties',
          point: '[class*="SidebarWrapper_toolbar"] [title="Checks"]',
          dialog: {
            selector: '#propsTable',
            content: 'Add any other valuable information about this material.',
          },
        },
        {
          onEnter: () => {
            self.selectPanel('checks');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          dialog: {
            selector: '[class*="SidebarWrapper_panelsOuter"]',
            content: 'Try and complete all the tasks listed below to help keep better track of your materials and increase confidence during production, distribution and sales.',
          },
        },
      ],
      messages,
      settings,
    };
  },
  computed: {
    ...mapState(['isMobile']),
    ...mapState('chromeEditor', ['panel']),
    ...mapState(storeName, ['data', 'vendors']),
    assetRecord() {
      return {
        type: ICategory.Material,
        type_id: this.data.id,
      };
    },
    materialId() {
      return this.$route.params.materialId;
    },
    country(): string {
      return (countries as any)[this.data.origin];
    },
    listOfChecks() {
      return checks(MaterialChecks, this.data);
    },
    washing: {
      get() {
        // create data structure to match wash icons signature
        const symbols = {} as { [key: string]: string };
        for (const category in washSymbols) {
          const value = this.data[`sym_${category.toLowerCase()}`];
          if (value) {
            symbols[category] = value.split(',');
          }
        }
        return symbols;
      },
      set(washing: any) {
        for (const category in washing) {
          const key = `sym_${category.toLowerCase()}`;
          this.data[key] = washing[category].join(',') || null;
        }
      },
    },
  },
  methods: {
    ...mapActions('chromeEditor', [
      'selectPanel',
      'toggleSidebar',
    ]),
    ...mapActions(storeName, [
      'create',
      'load',
    ]),
    askDelete() {
      const { id, name } = this.data;
      askDelete
        .bind(this)(id, name)
        .then((deleted: boolean) => (
          deleted && this.$router.push({
            name: 'materials',
          })
        ));
    },
    createRecord() {
      createMaterial(this);
    },
    async fetchData() {
      // load data, populate
      const result: IMaterial = await this.load(this.materialId);
      await this.assetComposerSyncAssets(
        result.assets.map(asset => ({
          ...asset,
          type_id: this.data.id,
        })),
      );
      return result;
    },
    print() {
      window.print();
    },
    selectRef(name: string) {
      this.selectPanel('info');

      // wait for animation to finish before focusing on element
      setTimeout(() => {
        const { $el } = this.$refs[name][0];
        $el.querySelector('input,textarea,[tabindex]').focus();
      }, 500);
    },
    save() {
      const promise = this.$store
        .dispatch(`${storeName}/save`)
        .catch((error: any) => {
          console.error(error);

          this.$store.commit('dialog/CREATE', {
            title: 'Save error',
            content: messages.ERROR_MATERIAL_SAVE,
            compact: true,
            delay: POST_LOADER_DELAY,
            buttons: [{
              text: 'Close',
              click: (message: any) => message.close(),
            }],
          });
        });

      this.$store.commit('loader/ADD', {
        title: 'Saving',
        promise,
      });
    },
  },
  components: {
    AssetManagerBox,
    AssetManagerRegion,
    AssetManagerSlideshow,
    CheckList,
    ChromeEditor,
    ChromeEditorPanel,
    FieldBase,
    FullscreenButton,
    GridColumn,
    GridForm,
    GridRow,
    PricingTable,
    PhysicalProps,
    ProductUsePanel,
    PropsTable,
    ShowGuide,
    SidebarPanel,
    VendorEditorPanel,
    VueAgile,
    WashIcons,
    WashSelector,
  },
} as IView);
